import React from "react";
import { ArrowDownward, ArrowUpward, DeleteOutline } from "@mui/icons-material";
import { Box, IconButton, TextField, Tooltip } from "@mui/material";
import { WorkflowTask } from "./workflowBuilderData";
import WorkflowPersonSourceField, { WorkflowPersonValue } from "./WorkflowPersonSourceField";
import { useWorkflowReadOnly } from "./WorkflowReadOnlyContext";

interface WorkflowTaskRowProps {
  task: WorkflowTask;
  index: number;
  count: number;
  onChange: (id: string, patch: Partial<WorkflowTask>) => void;
  onMove: (id: string, direction: "up" | "down") => void;
  onRemove: (id: string) => void;
}

const WorkflowTaskRow: React.FC<WorkflowTaskRowProps> = ({
  task,
  index,
  count,
  onChange,
  onMove,
  onRemove,
}) => {
  const viewOnly = useWorkflowReadOnly();
  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(task.id, { [event.target.name]: event.target.value });
  };

  const handleAssigneeChange = ({ source, reference }: WorkflowPersonValue) => {
    if (source) onChange(task.id, { assigneeSource: source, assignee: reference });
  };

  const handleMoveUp = () => onMove(task.id, "up");
  const handleMoveDown = () => onMove(task.id, "down");
  const handleRemove = () => onRemove(task.id);

  return (
    <Box className="workflow-canvas-dialog__task-card">
      <Box className="workflow-canvas-dialog__task-heading">
        <TextField
          disabled={viewOnly}
          name="title"
          label="Task"
          value={task.title}
          onChange={handleTextChange}
          placeholder="Task title"
          size="small"
          fullWidth
        />
        <Box className="workflow-canvas-dialog__task-tools">
          <Tooltip title="Move up" placement="top">
            <span className="d-inline-flex">
              <IconButton onClick={handleMoveUp} disabled={viewOnly || index === 0}>
                <ArrowUpward />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Move down" placement="top">
            <span className="d-inline-flex">
              <IconButton onClick={handleMoveDown} disabled={viewOnly || index === count - 1}>
                <ArrowDownward />
              </IconButton>
            </span>
          </Tooltip>
          {!viewOnly && (
            <Tooltip title="Delete task" placement="top">
              <IconButton onClick={handleRemove}>
                <DeleteOutline />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <TextField
        disabled={viewOnly}
        name="description"
        label="Description or URL"
        value={task.description}
        onChange={handleTextChange}
        placeholder="Describe the work or enter a https:// link"
        size="small"
        fullWidth
      />

      <Box className="workflow-canvas-dialog__task-assignee">
        <WorkflowPersonSourceField
          idPrefix={`${task.id}-assignee`}
          sourceLabel="Assign to"
          value={{ source: task.assigneeSource, reference: task.assignee }}
          onChange={handleAssigneeChange}
        />
      </Box>
    </Box>
  );
};

export default WorkflowTaskRow;
